import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  type ContextType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { AlertContext } from '../../../../contexts/alert-context';
import type { Order } from '../../../../data/order';
import type { OrderOptions } from '../../../../data/order-options';
import type { Product } from '../../../../data/product';
import {
  useBatchSetOrderItems,
  useDeleteOrder,
  useSetOrderOptionsV2,
} from '../../../../hooks/orders';
import { useProducts } from '../../../../hooks/products';
import { newerOrder, orderQueryOptions, writeOrderCache } from '../../../../query/query-options';
import { storeLayout } from '../../../../router';
import {
  classifyOrderConflict,
  type ItemUpdate,
  OrderVersionConflictError,
} from '../../../../services/stores';
import { itemHasUserCount, type ProductOrderItem } from '../data/product-order-item';
import type { EditOrderPopulatedProperties, OrderItemsViewModel } from '../types';
import { getSuggestedQuantities } from '../utils';
import { newUserFamilyId, userFamilyIdForBody } from '../utils/family-id';

// The 500ms settle window for the item overlay's coalesced PATCH. There is no
// separate flush-on-blur trigger (see the brief) — v1's 16-field contract has
// no room for one, and this settle plus the unmount flush below cover the
// same "don't lose the tail" requirement.
export const FLUSH_SETTLE_MS = 500;
// v1's options save uses the same 3000ms settle (use-order-items.ts:124) — kept
// identical so the options form doesn't fire noticeably more or less often
// across the v1/v2 fork.
export const OPTIONS_SETTLE_MS = 3000;

// The PATCH .../items wire type. `null` clears a user override back to the
// server suggestion — a different state from an explicit 0 — so this can't
// collapse to `number | undefined` the way ProductOrderItem's fields do.
type PendingEdit = { cases: number | null; units: number | null };

// Tracks whether a flush (items or options) is currently awaiting its
// response, and whether another one was requested while it was outstanding.
type FlushGuard = { inFlight: boolean; pending: boolean };

/**
 * Run `body` under a one-at-a-time guard, so at most one PATCH per order is
 * ever outstanding.
 *
 * Serializing is what makes the "a successful response overwrites a newer
 * edit" race structurally impossible rather than merely unlikely: with two
 * PATCHes in flight, the earlier one resolving last still wins the cache
 * write, and with the overlay already cleared by the later one nothing
 * re-applies the newer value — the row silently reverts, with no error.
 *
 * A call made while one is outstanding only records that a follow-up is owed;
 * the outstanding call runs it the instant it settles. Leaving it to the next
 * debounce cycle instead would strand the tail if the user stopped typing.
 */
async function runGuarded(guard: FlushGuard, body: () => Promise<void>): Promise<void> {
  if (guard.inFlight) {
    guard.pending = true;

    return;
  }

  guard.inFlight = true;
  try {
    await body();
  } finally {
    guard.inFlight = false;
    if (guard.pending) {
      guard.pending = false;
      void runGuarded(guard, body);
    }
  }
}

/**
 * The shared engine for the two flush paths (items, options). Each call gets
 * its own FlushGuard — items and options PATCH different endpoints and may
 * overlap — but the lifecycle around the guard is identical and subtle, so it
 * lives here once: snapshot the pending value, send it, then `confirm` — keep
 * only what the user re-typed while the request was in flight.
 *
 * On success the confirmed value is written to `stateRef` SYNCHRONOUSLY, not
 * only through `setState`: runGuarded's `finally` can re-run the body with no
 * `await` in between, and that re-run reads `stateRef.current` before React
 * has re-rendered from the `setState` call. A functional update
 * (`setState((current) => ...)`) would look right but isn't — the follow-up
 * flush would read the pre-confirm value and re-send what this one just
 * confirmed. Do not "simplify" the ref write away; it silently reintroduces
 * that redundant PATCH.
 */
function useGuardedFlush<Snapshot, Pending>(config: {
  stateRef: { current: Pending };
  setState: (next: Pending) => void;
  // Reads the value to send off the current pending state; null = nothing to
  // flush. Must COPY any mutable value — the snapshot has to survive later
  // pending-state writes, including the ones its own confirm performs.
  snapshot: (current: Pending) => Snapshot | null;
  send: (snapshot: Snapshot) => Promise<unknown>;
  // The pending state left over once `sent` is confirmed by the server.
  confirm: (current: Pending, sent: Snapshot) => Pending;
  onError: (error: unknown) => void;
}): () => Promise<void> {
  const guard = useRef<FlushGuard>({ inFlight: false, pending: false });
  // Read through a ref inside the flush body so the returned callback keeps a
  // stable identity while always seeing the latest config.
  const configRef = useRef(config);
  configRef.current = config;

  return useCallback(
    () =>
      runGuarded(guard.current, async () => {
        const { stateRef, setState, snapshot, send, confirm, onError } = configRef.current;
        const sent = snapshot(stateRef.current);
        if (sent === null) {
          return;
        }

        try {
          await send(sent);
          const next = confirm(stateRef.current, sent);
          stateRef.current = next;
          setState(next);
        } catch (error) {
          onError(error);
        }
      }),
    []
  );
}

// `useAllocateOrderDeal` (hooks/orders.ts) is deliberately NOT called here.
// Deal allocation writes through `setOrderItem` like any other edit, so it
// rides the same overlay and coalesces into the same batch PATCH — that
// coalescing is the point of this MR, so a separate allocate call here would
// reintroduce the contention this hook exists to remove.

/**
 * Build the per-product working rows from the cached order. This is
 * `initOrderItems` (`data/product-order-item.ts`) minus `getSuggestedQuantities`
 * — v2's suggestions arrive from the server on every item, which is also why
 * this takes no `options`: nothing in a v2 row is derived client-side from
 * them. Keeping options out of the signature is what lets a Reorder Settings
 * edit (new `options` identity per field change) skip this walk entirely.
 *
 * Maps over `products` (the full vendor catalog), not `order.items` (sparse):
 * mapping the other way would drop every product the server has no item for.
 *
 * The overlay is deliberately NOT applied here: on a 13k-product store this
 * walk runs only when the server order or the catalog change, while the
 * overlay changes on every keystroke. `applyOverlay` below is the
 * per-keystroke half.
 */
function buildRows(order: Order, products: Product[]): ProductOrderItem[] {
  const itemsByProductId = new Map(order.items.map((item) => [item.product.id, item]));

  return products.map((product) => ({ ...itemsByProductId.get(product.id), product }));
}

/**
 * Overlay the pending edits onto the built rows, last, so a typed value wins
 * over whatever the cache holds — including a refetch that lands mid-typing.
 *
 * Rows with no pending edit keep their identity, matching v1's `replaceItem`
 * (`data/product-order-item.ts`): a keystroke allocates one row object, not
 * one per product in the catalog.
 */
function applyOverlay(
  rows: ProductOrderItem[],
  overlay: Map<string, PendingEdit>
): ProductOrderItem[] {
  if (overlay.size === 0) {
    return rows;
  }

  return rows.map((row) => {
    const edit = overlay.get(row.product.id);
    if (!edit) {
      return row;
    }

    return { ...row, cases: edit.cases ?? undefined, units: edit.units ?? undefined };
  });
}

// Drop overlay entries that were just confirmed by a successful flush — but
// only the ones the user hasn't re-typed since the flush started. Comparing
// values (not just deleting every key the flush covered) is what keeps an
// edit made during an in-flight request from being silently clobbered by
// that request's own success.
function dropConfirmed(
  current: Map<string, PendingEdit>,
  flushed: Map<string, PendingEdit>
): Map<string, PendingEdit> {
  const next = new Map(current);

  for (const [productId, sent] of flushed) {
    const latest = next.get(productId);
    if (latest && latest.cases === sent.cases && latest.units === sent.units) {
      next.delete(productId);
    }
  }

  return next;
}

type FlushErrorContext = {
  error: unknown;
  storeId: string;
  orderId: string;
  queryClient: QueryClient;
  navigate: ReturnType<typeof useNavigate>;
  addErrorAlert: ContextType<typeof AlertContext>['addErrorAlert'];
};

// Both v2 mutations that flush from this hook (items, options) can fail with
// the same two 409s, and both need the same recovery — shared here so the two
// flush functions don't duplicate the discriminator (project CLAUDE.md: reuse
// before you write; a pre-push hook blocks exactly this kind of duplication).
function handleFlushError({
  error,
  storeId,
  orderId,
  queryClient,
  navigate,
  addErrorAlert,
}: FlushErrorContext): void {
  if (error instanceof OrderVersionConflictError) {
    // Version-conflict exhaustion: the response IS the server's current
    // truth. The mutation's onError deliberately skips toasting AND this
    // cache write for exactly this error (see useOrderMutationV2 in
    // hooks/orders.ts), so do it here, and leave the overlay alone — buildRows
    // re-applies it on top. No re-PATCH: the next trigger (next edit, or
    // unmount) resends it.
    //
    // writeOrderCache lands it in both caches through the newerOrder version
    // gate, same as every other order write: a 409 on one guard can be in
    // flight alongside a SUCCESSFUL, newer response on the other guard (items
    // vs options), and if that success lands first, this rebase must not
    // regress the cache below it.
    writeOrderCache(queryClient, storeId, orderId, error.latest);

    return;
  }

  if (classifyOrderConflict(error) === 'not-draft') {
    // The other 409, ORDER_NOT_DRAFT: the order was submitted or cancelled
    // elsewhere. Only this code navigates — a 409 we don't recognize
    // classifies as null and gets the mutation factory's generic toast, so a
    // new conflict cause can't silently bounce the user off an editable
    // order. Mirrors v1's refreshOrder
    // navigate+alert (use-order-items.ts:280-288). The mutation factory also
    // skips its generic toast for this status (hooks/orders.ts), so this is
    // the only alert the user sees for it.
    navigate({
      to: '/stores/$storeId/orders/$orderId/summary',
      params: { storeId, orderId },
    });
    addErrorAlert('Order has been submitted and can no longer be edited.', {
      keepAfterRouteChange: true,
    });

    return;
  }

  // 400 validation or network failure: nothing to do here. The mutation
  // factory already toasted and reported to Datadog, so the overlay stays
  // untouched and the next trigger (next keystroke, or unmount) retries it.
  // Not scheduling a retry here is what keeps a persistently-failing endpoint
  // from becoming an unbounded settle-interval retry loop.
}

export function useOrderItemsV2(properties: EditOrderPopulatedProperties): OrderItemsViewModel {
  const { store, products } = properties;
  const orderId = properties.order.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addErrorAlert } = useContext(AlertContext);
  const { storeService } = storeLayout.useRouteContext();

  const { data, dataUpdatedAt } = useQuery({
    ...orderQueryOptions(store.id, orderId),
    queryFn: async () => {
      // Match the codebase's established useQuery error convention (see
      // useOrders in hooks/orders.ts): toast, then rethrow so TanStack keeps
      // its own error/retry state. Without this, a failed/offline fetch left
      // `data` undefined with no toast at all — the page just kept editing
      // silently against `properties.order`, possibly forever.
      const fetched = await storeService
        .getOrderByStore(store.id, orderId)
        .catch((error: Error) => {
          addErrorAlert(`Error loading order: ${error.message}`, { cause: error });
          throw error;
        });

      // Guard against this GET resolving after a newer PATCH response is
      // already cached (e.g. a window-focus refetch that was in flight when a
      // save landed) — see newerOrder's comment. Without this, whichever
      // response arrives last wins the cache regardless of which is newer.
      const cached = queryClient.getQueryData<Order>(orderQueryOptions(store.id, orderId).queryKey);

      return newerOrder(cached, fetched);
    },
  });
  // OrderItemsViewModel.order is never undefined; properties.order (from the
  // orders list query) is a real order, just possibly one refetch stale.
  // Deliberately not `initialData` — TanStack would treat that as fresh and
  // suppress the mount fetch for the whole staleTime.
  const order = data ?? properties.order;

  // dataUpdatedAt is 0 until the query resolves once. v1 hid this window
  // behind `calculating: true` for its first 300ms; v2 reports `calculating`
  // as always false (rows derive synchronously), which removes that gate — so
  // falling back straight to `new Date(0)` would render "56 years ago" on
  // every first paint. Fall back to mount time instead until a real value
  // lands.
  const [mountedAt] = useState(() => new Date());
  const lastRefreshed = dataUpdatedAt === 0 ? mountedAt : new Date(dataUpdatedAt);

  // Edits the user has typed that the server hasn't confirmed yet. Keyed by
  // product so repeated edits to one row collapse to its last value — this
  // map IS the flush payload, and it's what survives a refetch landing
  // mid-typing (buildRows applies it last, on top of whatever order is
  // cached). Kept in state, not a ref, so rows re-derive when it changes.
  const [overlay, setOverlay] = useState<Map<string, PendingEdit>>(new Map());
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  const [pendingOptions, setPendingOptions] = useState<OrderOptions | null>(null);
  const pendingOptionsRef = useRef(pendingOptions);
  pendingOptionsRef.current = pendingOptions;
  const options = pendingOptions ?? order.options;

  const batchSet = useBatchSetOrderItems();
  const setOrderOptionsMutation = useSetOrderOptionsV2();
  const { updateProduct, saving: productSaving } = useProducts();
  const deleteOrder = useDeleteOrder();

  // Two stages on purpose — see buildRows. The expensive walk (13k products)
  // is keyed on the server data alone; only the cheap overlay patch re-runs
  // while the user types, and options edits touch neither stage.
  const baseRows = useMemo(() => buildRows(order, products), [order, products]);
  const orderItems = useMemo(() => applyOverlay(baseRows, overlay), [baseRows, overlay]);

  // Dev-only: v2's suggestions come from the server on each item, so this
  // never runs in production and never reaches Datadog RUM. It exists so a
  // real flipped store can be driven in dev against live data with any
  // divergence from the frozen client-side math (getSuggestedQuantities)
  // surfaced in the console. A separate effect, not part of buildRows: the
  // compare needs `options`, and threading options into the row build would
  // put the 13k-row walk back on the Reorder Settings edit path.
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const productsById = new Map(products.map((product) => [product.id, product]));
    for (const item of order.items) {
      const product = productsById.get(item.product.id);
      if (!product) {
        continue;
      }

      const clientSide = getSuggestedQuantities(product, options);
      if (
        clientSide.suggested_cases !== item.suggested_cases ||
        clientSide.suggested_units !== item.suggested_units
      ) {
        console.warn(
          `[order-edit-v2] suggestion divergence for ${product.id}: server ` +
            `${item.suggested_cases}c/${item.suggested_units}u vs client ` +
            `${clientSide.suggested_cases}c/${clientSide.suggested_units}u`
        );
      }
    }
  }, [order, products, options]);

  const handleError = useCallback(
    (error: unknown) =>
      handleFlushError({ error, storeId: store.id, orderId, queryClient, navigate, addErrorAlert }),
    [store.id, orderId, queryClient, navigate, addErrorAlert]
  );

  const flush = useGuardedFlush<Map<string, PendingEdit>, Map<string, PendingEdit>>({
    stateRef: overlayRef,
    setState: setOverlay,
    snapshot: (current) => (current.size === 0 ? null : new Map(current)),
    send: (snapshot) =>
      batchSet.mutateAsync({
        storeId: store.id,
        orderId,
        updates: [...snapshot].map(
          ([product_id, edit]): ItemUpdate => ({
            product_id,
            cases: edit.cases,
            units: edit.units,
          })
        ),
      }),
    // Drop only what this flush covered AND the user hasn't re-typed since —
    // see dropConfirmed. The mutation's onSuccess already wrote the response
    // into the order cache, so `order` picks it up on its own.
    confirm: dropConfirmed,
    onError: handleError,
  });

  const debouncedFlush = useDebouncedCallback(flush, FLUSH_SETTLE_MS);

  const setOrderItem = useCallback(
    (item: ProductOrderItem) => {
      setOverlay((current) => {
        const next = new Map(current);
        next.set(item.product.id, { cases: item.cases ?? null, units: item.units ?? null });

        return next;
      });
      debouncedFlush();
    },
    [debouncedFlush]
  );

  const flushOptions = useGuardedFlush<OrderOptions, OrderOptions | null>({
    stateRef: pendingOptionsRef,
    setState: setPendingOptions,
    snapshot: (current) => current,
    send: (snapshot) =>
      setOrderOptionsMutation.mutateAsync({ storeId: store.id, orderId, options: snapshot }),
    // Same "only if unchanged since" rule as dropConfirmed: clear only if the
    // user hasn't picked newer options while the PATCH was in flight.
    confirm: (current, sent) => (current === sent ? null : current),
    onError: handleError,
  });

  const debouncedFlushOptions = useDebouncedCallback(flushOptions, OPTIONS_SETTLE_MS);

  const setOptions = useCallback(
    (nextOptions: OrderOptions) => {
      setPendingOptions(nextOptions);
      debouncedFlushOptions();
    },
    [debouncedFlushOptions]
  );

  // Fire-and-forget on unmount (navigate away, tab close): the component is
  // going away, but a typed-and-not-yet-settled edit must not be lost. A ref
  // latch (mirroring use-order-items.ts's refreshOrderRef) is required here
  // because a plain `useEffect(() => () => flush(), [flush])` cleanup fires on
  // every identity change of `flush`, not just unmount.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const flushOptionsRef = useRef(flushOptions);
  flushOptionsRef.current = flushOptions;

  useEffect(() => {
    return () => {
      void flushRef.current();
      void flushOptionsRef.current();
    };
  }, []);

  // family_id lives on Product, not OrderItem, so it's persisted via
  // updateProduct (React Query cache patch), same as v1. Unlike v1, no local
  // orderItems patch is needed afterward: rows derive from `products`, which
  // the products cache patch already updated.
  const patchProductFamilyId = useCallback(
    async (product: Product, nextFamilyId: string) => {
      await updateProduct({ ...product, family_id: nextFamilyId });
    },
    [updateProduct]
  );

  const joinProductToFamily = useCallback(
    (product: Product, familyBody: string) =>
      patchProductFamilyId(product, userFamilyIdForBody(familyBody)),
    [patchProductFamilyId]
  );

  const removeProductFromFamily = useCallback(
    (product: Product) => patchProductFamilyId(product, newUserFamilyId()),
    [patchProductFamilyId]
  );

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const promptDeleteOrder = useCallback(() => {
    setShowCancelConfirm(true);
  }, []);

  const dismissCancelConfirm = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  const confirmDeleteOrder = useCallback(() => {
    setShowCancelConfirm(false);
    deleteOrder(order).then((success) => {
      if (success) {
        navigate({
          to: '/stores/$storeId/orders',
          params: { storeId: order.store_id },
        });
      }
    });
  }, [deleteOrder, navigate, order]);

  const resetOrder = useCallback(() => {
    const userItems = orderItems.filter(itemHasUserCount);
    if (userItems.length === 0) {
      return;
    }

    const response = confirm(
      `This will reset changes you have made to ${userItems.length} item(s) this order. Are you sure you want to continue?`
    );
    if (!response) {
      return;
    }

    // Clear every touched product's overlay entry in one Map, matching
    // setOrderItem's own mapping of `undefined` -> `null` (the PATCH wire
    // value that clears a user override back to the server suggestion) —
    // building one Map and flushing once avoids cloning the whole overlay
    // once per touched item, which on a large manually-edited order meant a
    // full Map copy per row inside this synchronous handler.
    setOverlay((current) => {
      const next = new Map(current);
      for (const orderItem of userItems) {
        next.set(orderItem.product.id, { cases: null, units: null });
      }

      return next;
    });
    debouncedFlush();
  }, [orderItems, debouncedFlush]);

  return {
    order,
    orderItems,
    setOrderItem,
    options,
    setOptions,
    joinProductToFamily,
    removeProductFromFamily,
    productSaving,
    // Rows derive synchronously from the cache + overlay — no seed to wait on.
    calculating: false,
    resetOrder,
    showCancelConfirm,
    promptDeleteOrder,
    dismissCancelConfirm,
    confirmDeleteOrder,
    lastRefreshed,
    // MR3 deletes the poll on this path entirely; nothing to pause.
    paused: false,
  };
}
